'use client';

import React from 'react';
import { Factory, Ship, Truck, Box, Globe, Shield } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import Link from 'next/link';
import { usePageView } from '@/lib/hooks/usePageView';

interface UseCaseCardProps {
  title: string;
  description: string;
  examples: string[];
  icon: React.ElementType;
  benefits: string[];
}

const UseCaseCard = ({ title, description, examples, icon: Icon, benefits }: UseCaseCardProps) => {
  return (
    <div className="bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow">
      <div className="flex items-center gap-4 mb-4">
        <div className="p-2 bg-orange-100 rounded-lg">
          <Icon className="h-6 w-6 text-orange-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
      </div>
      <p className="text-gray-600 mb-4">{description}</p>
      <div className="mb-4">
        <h4 className="font-semibold text-gray-900 mb-2">Example Applications:</h4>
        <ul className="list-disc list-inside text-gray-600 space-y-1">
          {examples.map((example, index) => (
            <li key={index}>{example}</li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="font-semibold text-gray-900 mb-2">Key Benefits:</h4>
        <ul className="list-disc list-inside text-gray-600 space-y-1">
          {benefits.map((benefit, index) => (
            <li key={index}>{benefit}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default function UseCases() {
  usePageView();
  
  const useCases = [
    {
      title: 'Manufacturers',
      description: 'Track global shipments and optimize logistics costs.',
      icon: Factory,
      examples: [
        'Real-time shipment tracking',
        'Instant carrier rate comparison',
        'Automated duty calculations',
        'Supplier shipping performance'
      ],
      benefits: [
        'Reduce transportation costs',
        'Minimize customs delays',
        'Improve delivery accuracy',
        'Optimize carrier selection'
      ]
    },
    {
      title: 'Global Importers',
      description: 'Manage international shipping costs and compliance.',
      icon: Ship,
      examples: [
        'Landed cost calculation',
        'Duty and tariff estimation',
        'Multi-carrier rate shopping',
        'Document digitization'
      ],
      benefits: [
        'Accurate cost forecasting',
        'Better profit margins',
        'Faster customs clearance',
        'Competitive shipping rates'
      ]
    },
    {
      title: 'Freight Forwarders',
      description: 'Streamline quote management and shipment tracking.',
      icon: Truck,
      examples: [
        'Automated quote generation',
        'Real-time tracking updates',
        'Landed cost calculator',
        'Document automation'
      ],
      benefits: [
        'Faster quote turnaround',
        'Increased quote accuracy',
        'Enhanced customer service',
        'Reduced operational costs'
      ]
    },
    {
      title: '3PL/4PL Providers',
      description: 'Optimize logistics operations and client services.',
      icon: Box,
      examples: [
        'Multi-carrier management',
        'Rate comparison tools',
        'Client tracking portal',
        'Cost allocation tracking'
      ],
      benefits: [
        'Competitive rate offering',
        'Improved client retention',
        'Enhanced visibility',
        'Streamlined operations'
      ]
    },
    {
      title: 'Customs Brokers',
      description: 'Streamline compliance and cost calculations.',
      icon: Shield,
      examples: [
        'Automated duty calculation',
        'Tariff code lookup',
        'Real-time rate updates',
        'Document verification'
      ],
      benefits: [
        'Faster clearance times',
        'Accurate duty estimates',
        'Reduced compliance risks',
        'Improved client service'
      ]
    },
    {
      title: 'Trading Companies',
      description: 'Optimize international shipping and cost management.',
      icon: Globe,
      examples: [
        'Landed cost modeling',
        'Multi-carrier quotes',
        'Duty optimization',
        'Route planning'
      ],
      benefits: [
        'Better margin control',
        'Optimized shipping costs',
        'Improved compliance',
        'Enhanced efficiency'
      ]
    }
  ];

  return (
    <div className="container mx-auto px-4 py-16">
      {/* Hero Section */}
      <div className="text-center mb-16">
        <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl mb-6">
        Transform Supply Chain Visibility Across Industries
        </h1>
        <p className="text-xl text-black max-w-3xl mx-auto">
          Discover how our intelligent platform optimizes tracking and compliance for various supply chain scenarios.
        </p>
      </div>

      {/* Use Cases Grid */}
      <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 mb-16">
        {useCases.map((useCase, index) => (
          <UseCaseCard key={index}
{...useCase} />
        ))}
      </div>

      {/* CTA Section */}
      <div className="text-center bg-orange-50 rounded-2xl p-8 md:p-12">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
        Ready to Transform Your Supply Chain?
        </h2>
        <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
        Join leading organizations worldwide using our platform to optimize their supply chain operations.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button size="lg"
className="bg-orange-600 hover:bg-orange-700">
            <Link href={`/dashboard/${process.env.NEXT_PUBLIC_DEMO_VULCAN_ID}`}>See It In Action</Link>
          </Button>
          <Button variant="outline"
size="lg"
className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <Link href="/contact">Contact Sales</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
